import { useEffect, useState } from "react";
import { Box, Card, Grid, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { BarChart as BarChartIcon, FilterAlt } from "@mui/icons-material";
import moment from "moment";
import Loader from "../../../../components/Loader/Loader";
import { getUserData } from "../../../../utils/FunctionHelpers";
import { withSnackbar } from "../../../../utils/SnackbarHOC";
import { getPilotageConsultants, getPilotageConsultantsDetail } from "../../../Services/PilotageService";
import ProductionDetailModal from "../../../Pilotage/Components/ProductionDetailModal";
import DateRangeFilter from "./Components/DateRangeFilter";
import KpiPerformanceCard from "./Components/KpiPerformanceCard";
import ModernEvolutionChart from "./Components/ModernEvolutionChart";
import ModernProductionChart from "./Components/ModernProductionChart";
import ProductionFunnelChart from "./Components/ProductionFunnelChart";
import { METRIC_TYPES } from "./productionPalette";
import "./progressSection.css";

const initialProduction = [
  { label: "Sourcé", value: 0 },
  { label: "Envois", value: 0 },
  { label: "Révélations", value: 0 },
  { label: "Propales", value: 0 },
  { label: "Signatures", value: 0 },
];

const initialKpi = [
  { label: "TSO (Envois/Sourcé)", value: 0 },
  { label: "TR (Rév°/Envois)", value: 0 },
  { label: "TP (Propales/Envois)", value: 0 },
  { label: "TS (Sign/Envois)", value: 0 },
];

const initialEvolutionData = Array(12)
  .fill(0)
  .map((_, index) => {
    const month = moment().month(index).format("MMM");
    return {
      Sourcing: 0,
      Envois: 0,
      Révélations: 0,
      Propales: 0,
      Signatures: 0,
      month: month.charAt(0).toUpperCase() + month.slice(1),
    };
  });

const ProgressSection = ({ snackbarShowMessage }) => {
  const [loading, setLoading] = useState(true);
  const [production, setProduction] = useState(initialProduction);
  const [kpi, setKpi] = useState(initialKpi);
  const [evolutionData, setEvolutionData] = useState(initialEvolutionData);
  const [startDate, setStartDate] = useState(moment().startOf("year").toDate());
  const [endDate, setEndDate] = useState(new Date());
  const [productionView, setProductionView] = useState("bars");
  const [detailModal, setDetailModal] = useState({
    open: false,
    metricType: null,
    data: [],
    loading: false,
    error: null,
  });

  useEffect(() => {
    const uuid = getUserData()?.uuid;
    if (!uuid) {
      setLoading(false);
      return;
    }

    const queryParams =
      `?consultants[]=${uuid}` +
      `&startDate=${moment(startDate).format("YYYY-MM-DD")}` +
      `&endDate=${moment(endDate).format("YYYY-MM-DD")}`;

    setLoading(true);
    getPilotageConsultants(queryParams)
      .then(({ data }) => {
        const { Production, KPI, Evolution } = data;
        setProduction([
          { label: "Sourcé", value: Production["Sourcing"] },
          { label: "Envois", value: Production["Envoi"] },
          { label: "Révélations", value: Production["Révélation"] },
          { label: "Propales", value: Production["Propale"] },
          { label: "Signatures", value: Production["Embauche/Integration"] },
        ]);
        setKpi([
          { label: "TSO (Envois/Sourcé)", value: KPI["TSO"] },
          { label: "TR (Rév°/Envois)", value: KPI["TR"] },
          { label: "TP (Propales/Envois)", value: KPI["TP"] },
          { label: "TS (Sign/Envois)", value: KPI["TS"] },
        ]);

        const localEvolutionData = Object.keys(Evolution).map((month) => ({
          Sourcing: Evolution[month]["Sourcing"],
          Envois: Evolution[month]["Envoi"],
          Révélations: Evolution[month]["Révélation"],
          Propales: Evolution[month]["Propale"],
          Signatures: Evolution[month]["Embauche/Integration"],
          month,
        }));
        setEvolutionData(localEvolutionData);
      })
      .catch(() => {
        snackbarShowMessage("Erreur de chargement des données d'avancement.", "error", 4000);
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate, snackbarShowMessage]);

  const handleProductionBarClick = (barIndex) => {
    const metricType = METRIC_TYPES[barIndex];
    const uuid = getUserData()?.uuid;
    if (!metricType || !uuid) return;

    setDetailModal({ open: true, metricType, data: [], loading: true, error: null });

    const queryParams =
      `?consultants[]=${uuid}` +
      `&startDate=${moment(startDate).format("YYYY-MM-DD")}` +
      `&endDate=${moment(endDate).format("YYYY-MM-DD")}` +
      `&type=${encodeURIComponent(metricType)}`;

    getPilotageConsultantsDetail(queryParams)
      .then(({ data }) => {
        setDetailModal((prev) => ({
          ...prev,
          loading: false,
          data: Array.isArray(data) ? data : (data.items ?? []),
        }));
      })
      .catch(() => {
        setDetailModal((prev) => ({
          ...prev,
          loading: false,
          error: "Erreur de chargement des données.",
        }));
      });
  };

  return (
    <>
      <Box className="progressSectionHeader">
        <Typography component="strong" className="progressSectionTitle">
          AVANCEMENT
        </Typography>
        <DateRangeFilter
          startDate={startDate}
          endDate={endDate}
          onChange={(newStart, newEnd) => {
            setStartDate(newStart);
            setEndDate(newEnd);
          }}
        />
      </Box>

      {loading ? (
        <Loader />
      ) : (
        <Grid container spacing={2} sx={{ marginTop: "0px" }}>
          <Grid item xs={12} md={5}>
            <Card className="progressCard" elevation={0}>
              <Box className="progressHeader">
                <Box className="progressHeaderLeft">
                  <BarChartIcon className="progressHeaderIcon" />
                  <Typography component="strong" className="progressHeaderTitle">
                    PRODUCTION
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={productionView}
                  onChange={(event, value) => value && setProductionView(value)}
                >
                  <ToggleButton value="bars" aria-label="Vue barres">
                    <BarChartIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="funnel" aria-label="Vue entonnoir">
                    <FilterAlt fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {productionView === "bars" ? (
                <ModernProductionChart data={production} onBarClick={handleProductionBarClick} />
              ) : (
                <ProductionFunnelChart data={production} onBarClick={handleProductionBarClick} />
              )}
            </Card>
          </Grid>
          <Grid item xs={12} md={7}>
            <KpiPerformanceCard rows={kpi} />
          </Grid>
          <Grid item xs={12}>
            <ModernEvolutionChart data={evolutionData} />
          </Grid>
        </Grid>
      )}

      <ProductionDetailModal
        open={detailModal.open}
        onClose={() => setDetailModal((prev) => ({ ...prev, open: false }))}
        metricType={detailModal.metricType}
        data={detailModal.data}
        loading={detailModal.loading}
        error={detailModal.error}
        metricCounts={production.map((item) => item.value)}
        onMetricChange={(metricType) => handleProductionBarClick(METRIC_TYPES.indexOf(metricType))}
      />
    </>
  );
};

export default withSnackbar(ProgressSection);
